import { TexturedMaterial } from './TexturedMaterial';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { SimpleSettingsProperty } from "../properties/SimpleSettingsProperty";
import * as THREE from 'three';
import { BlendMode } from './Material';
import { Logger } from '../core/Logger';
import { POIData } from '../poi/POIData';
import { Scene } from '../core/Scene';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';

export class YouTubeMaterial extends TexturedMaterial {
    // YouTube-specific properties
    public readonly autoplay: SimpleSettingsProperty<boolean>;
    public readonly loop: SimpleSettingsProperty<boolean>;
    public readonly muted: SimpleSettingsProperty<boolean>;
    public readonly startTime: SimpleSettingsProperty<number>; // Start time in seconds
    public readonly endTime: SimpleSettingsProperty<number>; // End time in seconds (0 = no end)
    public readonly quality: SimpleSettingsProperty<string>; // YouTube quality setting
    public readonly showControls: SimpleSettingsProperty<boolean>;
    public readonly blendMode: SimpleSettingsProperty<BlendMode>;
    
    // YouTube iframe and video state
    private _iframe: HTMLIFrameElement | null = null;
    private _videoElement: HTMLVideoElement | null = null;
    private _isPlaying: boolean = false;
    private _videoId: string | null = null;
    private _canvasElement: HTMLCanvasElement | null = null;
    private _context: CanvasRenderingContext2D | null = null;
    private _animationFrame: number | null = null;
    private _thumbnailLoaded: boolean = false;
    private _manuallyPaused: boolean = false;
    private _videoMode: 'thumbnail' | 'proxy' | 'iframe' | 'css3d' = 'thumbnail';
    public readonly playOnActive: SimpleSettingsProperty<boolean>;
    private _css3dObject: CSS3DObject | null = null; // CSS3DObject for 3D positioned iframe
    public readonly css3dScale: SimpleSettingsProperty<number>; // Manual scale override for CSS3D

    constructor(
        name: string = 'YouTubeMaterial',
        poiData: POIData,
        private poi?: any // POI reference for position/scale access
    ) {
        super('video', name, poiData);
        
        //Logger.Instance.Info(`🎬 YouTubeMaterial '${name}' created`);
        
        const groupName = "YouTube Control";
        this.autoplay = new SimpleSettingsProperty('Autoplay', null, false, groupName);
        this.loop = new SimpleSettingsProperty('Loop', null, true, groupName);
        this.muted = new SimpleSettingsProperty('Muted', null, true, groupName);
        this.startTime = new SimpleSettingsProperty("Start Time (s)", null, 0, groupName);
        this.endTime = new SimpleSettingsProperty("End Time (s)", null, 0, groupName);
        this.quality = new SimpleSettingsProperty("Quality", null, 'default', groupName);
        this.showControls = new SimpleSettingsProperty('Show Controls', null, false, groupName);
        this.playOnActive = new SimpleSettingsProperty('Play on Active', true, true, groupName);
        this.css3dScale = new SimpleSettingsProperty('CSS3D Scale Override', null, 1.0, groupName).withBounds(0.001, 10, 0.001);
        
        const renderGroup = "Rendering";
        this.blendMode = new SimpleSettingsProperty<BlendMode>('Blend Mode', 'normal', 'normal', renderGroup, 
            ['normal', 'additive', 'subtractive', 'multiply']);
        
        this.setupPropertyListeners();
    }

    protected override setupPropertyListeners(): void {
        super.setupPropertyListeners();
        
        // YouTube-specific property listeners
        this.autoplay.on(() => this.updateYouTubeParams());
        this.loop.on(() => this.updateYouTubeParams());
        this.muted.on(() => this.updateYouTubeParams());
        this.startTime.on(() => this.updateYouTubeParams());
        this.endTime.on(() => this.updateYouTubeParams());
        this.quality.on(() => this.updateYouTubeParams());
        this.showControls.on(() => this.updateYouTubeParams());
    }

    /**
     * Extract YouTube video ID from various YouTube URL formats
     */
    private extractYouTubeVideoId(url: string): string | null {
        const patterns = [
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&\n?#]+)/,
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^&\n?#]+)/,
            /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^&\n?#]+)/,
            /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([^&\n?#]+)/
        ];
        
        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        
        return null;
    }

    /**
     * Check if a URL is a YouTube URL
     */
    public static isYouTubeUrl(url: string): boolean {
        return /(?:youtube\.com|youtu\.be)/.test(url);
    }

    protected loadTexture(url: string): void {
        if (!url || this.isLoading) {
            return;
        }
        
        this.disposeTexture();
        
        const videoId = this.extractYouTubeVideoId(url);
        if (!videoId) {
            this.error = 'Invalid YouTube URL';
            Logger.Instance.Error(`YouTubeMaterial: Invalid YouTube URL: ${url}`);
            return;
        }
        
        this._videoId = videoId;
        this.isLoading = true;
        this.error = null;
        
        // Set YouTube standard aspect ratio (16:9)
        this.originalTextureAspectRatio = 16 / 9;
        this.aspectRatio.value = 16 / 9;
        
        //Logger.Instance.Info(`YouTubeMaterial: Loading YouTube video ${videoId} with 16:9 aspect ratio`);
        
        // Create a canvas to capture the YouTube video
        this._canvasElement = document.createElement('canvas');
        this._canvasElement.width = 1280;
        this._canvasElement.height = 720;
        this._context = this._canvasElement.getContext('2d');
        
        // Try to create actual HTML5 video element for real video playback
        this.tryCreateVideoElement(videoId);
        
        // Initially create canvas texture for thumbnail/fallback
        this._texture = new THREE.CanvasTexture(this._canvasElement);
        this._texture.wrapS = this.getThreeWrap(this.wrapS.value || 'repeat');
        this._texture.wrapT = this.getThreeWrap(this.wrapT.value || 'repeat');
        this._texture.magFilter = this.getThreeFilter(this.magFilter.value || 'linear');
        this._texture.minFilter = this.getThreeFilter(this.minFilter.value || 'linearMipmapLinear');
        this._texture.flipY = this.flipY.value ?? true;
        
        // Start with thumbnail
        this.startFrameCapture();
        
        this.isLoading = false;
        this.updateTextureProperties();
        
        //Logger.Instance.Info(`YouTubeMaterial: YouTube video ${videoId} loaded successfully`);
    }

    private buildYouTubeEmbedUrl(videoId: string): string {
        const params = new URLSearchParams();
        if (this.autoplay.value) params.set('autoplay', '1');
        if (this.muted.value) params.set('mute', '1');
        if (this.loop.value) {
            params.set('loop', '1');
            params.set('playlist', videoId);
        }
        if (this.startTime.value && this.startTime.value > 0) params.set('start', this.startTime.value.toString());
        if (this.endTime.value && this.endTime.value > 0) params.set('end', this.endTime.value.toString());
        if (!this.showControls.value) params.set('controls', '0');
        // Better UX params
        params.set('modestbranding', '1');
        params.set('rel', '0');
        params.set('playsinline', '1');
        // API & security
        params.set('enablejsapi', '1');
        params.set('origin', window.location.origin);
        
        // Use nocookie domain to reduce tracking and CSP friction
        return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
    }

    private updateYouTubeParams(): void {
        if (this._iframe && this._videoId) {
            const newUrl = this.buildYouTubeEmbedUrl(this._videoId);
            this._iframe.src = newUrl;
        }
    }

    private async tryCreateVideoElement(videoId: string): Promise<void> {
        try {
            //console.log(`🎬 Attempting to create HTML5 video element for ${videoId}...`);
            
            // Try to get video URLs (this is where the magic needs to happen)
            const videoUrls = await this.getYouTubeVideoUrls(videoId);
            
            if (videoUrls && videoUrls.length > 0) {
                // Create HTML5 video element
                this._videoElement = document.createElement('video');
                this._videoElement.crossOrigin = 'anonymous';
                this._videoElement.loop = this.loop.value ?? true;
                this._videoElement.muted = this.muted.value ?? true;
                this._videoElement.autoplay = false; // We'll control this manually
                this._videoElement.playsInline = true;
                this._videoElement.style.display = 'none';
                
                // Set video source
                this._videoElement.src = videoUrls[0];
                
                // Add to DOM (hidden)
                document.body.appendChild(this._videoElement);
                
                // Wait for video to be ready
                this._videoElement.addEventListener('loadeddata', () => {
                    //console.log(`✅ Video loaded successfully: ${videoId}`);
                    this.switchToVideoTexture();
                });
                
                this._videoElement.addEventListener('error', (e) => {
                    console.warn(`⚠️ Video failed to load:`, e);
                    this.fallbackToThumbnail();
                });
                
                this._videoMode = 'proxy';
                
            } else {
                console.warn(`⚠️ No video URLs found for ${videoId}`);
                this.fallbackToThumbnail();
            }
            
        } catch (error) {
            console.warn(`⚠️ Could not create video element:`, error);
            this.fallbackToThumbnail();
        }
    }

    private async getYouTubeVideoUrls(videoId: string): Promise<string[] | null> {
        try {
            // ⚠️ IMPORTANT: This is a simplified example
            // In reality, you'd need to use a YouTube proxy service or API
            // YouTube blocks direct video URL access due to terms of service
            
            //console.log(`🔍 Attempting to get video URLs for ${videoId}...`);
            
            // Option 1: Try a proxy service (you'd need to set this up)
            // const proxyUrl = `https://your-proxy-server.com/youtube/${videoId}`;
            
            // Option 2: Use a public API (if available and legal)
            // const apiUrl = `https://api.example.com/youtube/extract/${videoId}`;
            
            // For now, we'll return null to indicate we can't get direct URLs
            // This will fall back to thumbnail mode
            return null;
            
        } catch (error) {
            console.warn(`Failed to get YouTube video URLs:`, error);
            return null;
        }
    }

    private switchToVideoTexture(): void {
        if (!this._videoElement) return;
        
        //console.log(`🎬 Switching to VideoTexture mode`);
        
        // Dispose old texture
        if (this._texture) {
            this._texture.dispose();
        }
        
        // Create THREE.VideoTexture
        this._texture = new THREE.VideoTexture(this._videoElement);
        this._texture.wrapS = this.getThreeWrap(this.wrapS.value || 'repeat');
        this._texture.wrapT = this.getThreeWrap(this.wrapT.value || 'repeat');
        this._texture.magFilter = this.getThreeFilter(this.magFilter.value || 'linear');
        this._texture.minFilter = this.getThreeFilter(this.minFilter.value || 'linearMipmapLinear');
        this._texture.flipY = this.flipY.value ?? true;
        
        // Update material
        if (this._threeMaterial) {
            const material = this._threeMaterial as THREE.MeshStandardMaterial;
            material.map = this._texture;
            material.needsUpdate = true;
        }
        
        this._videoMode = 'proxy';
        //console.log(`✅ VideoTexture active`);
    }

    private fallbackToThumbnail(): void {
        //console.log(`📷 Falling back to thumbnail mode`);
        this._videoMode = 'thumbnail';
        // Continue with canvas-based thumbnail
    }

    private showYouTubePlayer(): void {
        if (this._iframe) {
            this._iframe.style.display = 'block';
            const closeButton = (this._iframe as any)._closeButton;
            if (closeButton) {
                closeButton.style.display = 'block';
            }
            //console.log(`📺 YouTube Player shown`);
        }
    }

    private hideYouTubePlayer(): void {
        if (this._iframe) {
            this._iframe.style.display = 'none';
            const closeButton = (this._iframe as any)._closeButton;
            if (closeButton) {
                closeButton.style.display = 'none';
            }
            this.pause(); // Pause when hiding
            //console.log(`📺 YouTube Player hidden`);
        }
    }

    private startFrameCapture(): void {
        if (!this._canvasElement || !this._context) return;
        
        const captureFrame = () => {
            if (!this._canvasElement || !this._context) return;
            
            try {
                if (this._videoMode === 'thumbnail' && this._thumbnailLoaded) {
                    // Thumbnail mode: stop animation once loaded
                    this._animationFrame = null;
                    return;
                } else if (this._videoMode === 'iframe' && this._isPlaying) {
                    // Iframe mode: try to capture video (probably won't work due to CORS)
                    this.tryCaptureLiveVideo();
                } else {
                    // Show placeholder while loading
                    this.drawYouTubePlaceholder();
                }
                
                if (this._texture) {
                    this._texture.needsUpdate = true;
                }
            } catch (error) {
                Logger.Instance.Error('YouTubeMaterial: Error capturing frame:', error as string);
            }
            
            // Continue animation if not in static thumbnail mode
            if (!(this._videoMode === 'thumbnail' && this._thumbnailLoaded)) {
                this._animationFrame = requestAnimationFrame(captureFrame);
            }
        };
        
        captureFrame();
    }

    private tryCaptureLiveVideo(): void {
        if (!this._iframe || !this._context || !this._canvasElement) return;
        
        try {
            // This will likely fail due to CORS, but worth trying
            // For now, we'll show the thumbnail with a "LIVE" indicator
            this.drawLiveVideoPlaceholder();
        } catch (error) {
            // Expected to fail - just show static thumbnail
            this.drawYouTubePlaceholder();
        }
    }

    private drawLiveVideoPlaceholder(): void {
        if (!this._context || !this._canvasElement) return;
        
        const ctx = this._context;
        const canvas = this._canvasElement;
        
        // Clear with dark background
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        
        // Draw "LIVE" indicator
        ctx.fillStyle = '#ff0000';
        ctx.font = 'bold 60px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('● LIVE', centerX, centerY - 50);
        
        // Draw YouTube logo
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.arc(centerX, centerY + 50, 40, 0, 2 * Math.PI);
        ctx.fill();
        
        // Play triangle
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(centerX - 15, centerY + 35);
        ctx.lineTo(centerX - 15, centerY + 65);
        ctx.lineTo(centerX + 15, centerY + 50);
        ctx.closePath();
        ctx.fill();
        
        // Video info
        if (this._videoId) {
            ctx.fillStyle = '#ffffff';
            ctx.font = '24px Arial, sans-serif';
            ctx.fillText(`Video: ${this._videoId}`, centerX, centerY + 120);
        }
    }

    private drawYouTubePlaceholder(): void {
        if (!this._context || !this._canvasElement) return;
        
        const ctx = this._context;
        const canvas = this._canvasElement;
        
        // Clear canvas with dark background
        ctx.fillStyle = '#181818';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw YouTube-style loading indicator (only if thumbnail isn't loaded yet)
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        
        // Draw play button circle
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.arc(centerX, centerY, 60, 0, 2 * Math.PI);
        ctx.fill();
        
        // Draw play triangle
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(centerX - 20, centerY - 25);
        ctx.lineTo(centerX - 20, centerY + 25);
        ctx.lineTo(centerX + 25, centerY);
        ctx.closePath();
        ctx.fill();
        
        // Draw "Loading YouTube..." text
        ctx.fillStyle = '#ffffff';
        ctx.font = '20px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Loading YouTube...', centerX, centerY + 100);
        
        // Draw video ID for debugging
        if (this._videoId) {
            ctx.font = '16px Arial, sans-serif';
            ctx.fillStyle = '#aaaaaa';
            ctx.fillText(`Video: ${this._videoId}`, centerX, centerY + 130);
        }
        
        // Load thumbnail once if not already loaded
        if (!this._thumbnailLoaded) {
            this.loadYouTubeThumbnail();
        }
        
        // Add interaction indicator
        if (this._videoMode === 'thumbnail') {
            // Draw "Activate POI to play" indicator
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.font = '24px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Navigate to POI to play YouTube video', centerX, centerY + 160);
        }
    }

    private async loadYouTubeThumbnail(): Promise<void> {
        if (!this._videoId || !this._context || !this._canvasElement) return;
        
        try {
            // Try high-quality thumbnail first, fallback to standard quality
            const thumbnailUrls = [
                `https://img.youtube.com/vi/${this._videoId}/maxresdefault.jpg`,
                `https://img.youtube.com/vi/${this._videoId}/hqdefault.jpg`,
                `https://img.youtube.com/vi/${this._videoId}/mqdefault.jpg`
            ];
            
            //console.log(`🎬 YouTubeMaterial: Loading thumbnail for video ${this._videoId}`);
            
            for (const thumbnailUrl of thumbnailUrls) {
                try {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    
                    const loadPromise = new Promise<void>((resolve, reject) => {
                        img.onload = () => {
                            if (this._context && this._canvasElement) {
                                // Clear canvas and draw thumbnail
                                this._context.clearRect(0, 0, this._canvasElement.width, this._canvasElement.height);
                                this._context.drawImage(img, 0, 0, this._canvasElement.width, this._canvasElement.height);
                                
                                // Draw YouTube play button overlay
                                this.drawPlayButtonOverlay();
                                
                                if (this._texture) {
                                    this._texture.needsUpdate = true;
                                }
                                this._thumbnailLoaded = true; // Mark as loaded
                                //console.log(`✅ YouTubeMaterial: Thumbnail loaded successfully for ${this._videoId}`);
                            }
                            resolve();
                        };
                        
                        img.onerror = () => reject(new Error(`Failed to load ${thumbnailUrl}`));
                    });
                    
                    img.src = thumbnailUrl;
                    await loadPromise;
                    return; // Success, exit loop
                    
                } catch (error) {
                    console.warn(`⚠️ YouTubeMaterial: Failed to load ${thumbnailUrl}, trying next...`);
                    continue;
                }
            }
            
            Logger.Instance.Warn(`YouTubeMaterial: Could not load any thumbnail for ${this._videoId}`);
            
        } catch (error) {
            Logger.Instance.Error('YouTubeMaterial: Error loading thumbnail:', error as string);
        }
    }

    private drawPlayButtonOverlay(): void {
        if (!this._context || !this._canvasElement) return;
        
        const ctx = this._context;
        const centerX = this._canvasElement.width / 2;
        const centerY = this._canvasElement.height / 2;
        
        // Semi-transparent overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(0, 0, this._canvasElement.width, this._canvasElement.height);
        
        // YouTube play button
        ctx.fillStyle = '#ff0000';
        ctx.beginPath();
        ctx.arc(centerX, centerY, 50, 0, 2 * Math.PI);
        ctx.fill();
        
        // Play triangle
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(centerX - 15, centerY - 20);
        ctx.lineTo(centerX - 15, centerY + 20);
        ctx.lineTo(centerX + 20, centerY);
        ctx.closePath();
        ctx.fill();
    }

    protected updateTextureProperties(): void {
        if (!this._texture) return;
        
        this._texture.repeat.set(this.repeatX.value ?? 1, this.repeatY.value ?? 1);
        this._texture.offset.set(this.offsetX.value ?? 0, this.offsetY.value ?? 0);
        this._texture.rotation = (this.rotation.value ?? 0) * Math.PI / 180;
        this._texture.wrapS = this.getThreeWrap(this.wrapS.value || 'repeat');
        this._texture.wrapT = this.getThreeWrap(this.wrapT.value || 'repeat');
        this._texture.magFilter = this.getThreeFilter(this.magFilter.value || 'linear');
        this._texture.minFilter = this.getThreeFilter(this.minFilter.value || 'linearMipmapLinear');
        this._texture.needsUpdate = true;
        
        if (this._threeMaterial) {
            const material = this._threeMaterial as THREE.MeshStandardMaterial;
            material.map = this._texture;
            material.needsUpdate = true;
        }
    }

    private getThreeWrap(wrap: string): THREE.Wrapping {
        switch (wrap) {
            case 'clampToEdge': return THREE.ClampToEdgeWrapping;
            case 'mirroredRepeat': return THREE.MirroredRepeatWrapping;
            default: return THREE.RepeatWrapping;
        }
    }

    private getThreeFilter(filter: string): THREE.TextureFilter {
        switch (filter) {
            case 'nearest': return THREE.NearestFilter;
            case 'nearestMipmapNearest': return THREE.NearestMipmapNearestFilter;
            case 'linearMipmapNearest': return THREE.LinearMipmapNearestFilter;
            case 'nearestMipmapLinear': return THREE.NearestMipmapLinearFilter;
            case 'linearMipmapLinear': return THREE.LinearMipmapLinearFilter;
            default: return THREE.LinearFilter;
        }
    }

    public override getThreeMaterial(): THREE.Material {
        if (!this._threeMaterial) {
            this._threeMaterial = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                side: this.getAppropriateThreeSide()
            }) as any;
        }
        
        const material = this._threeMaterial as THREE.MeshStandardMaterial;
        
        if (this._texture) {
            material.map = this._texture;
            material.needsUpdate = true;
        }
        
        return material;
    }

    protected disposeTexture(): void {
        // Stop frame capture
        if (this._animationFrame) {
            cancelAnimationFrame(this._animationFrame);
            this._animationFrame = null;
        }
        
        // Remove iframe from DOM
        if (this._iframe && this._iframe.parentNode) {
            this._iframe.parentNode.removeChild(this._iframe);
        }
        this._iframe = null;
        
        // Dispose Three.js texture
        if (this._texture) {
            this._texture.dispose();
            this._texture = null;
        }
        
        // Clean up canvas
        this._canvasElement = null;
        this._context = null;
        this._videoId = null;
        this._isPlaying = false;
        this._thumbnailLoaded = false; // Reset flag
        
        //Logger.Instance.Info('YouTubeMaterial: Texture disposed');
    }

    public override onUpdate(deltaTime: number): void {
        super.onUpdate(deltaTime);
        
        // Update texture if needed
        if (this._texture) {
            this._texture.needsUpdate = true;
        }
    }

    // Override Material base class methods for activation
    public override setActive(): void {
        //Logger.Instance.Info(`🎬 YouTube POI activated: ${this.name} (mode: ${this._videoMode})`);
        
        if (this._videoMode === 'proxy' && this._videoElement) {
            // Real video mode - play directly on 3D object
            if (this.playOnActive.value && !this._manuallyPaused) {
                this.play();
            }
        } else if (this._videoMode === 'thumbnail') {
            // Show CSS3D video player at POI's 3D position
            if (this._videoId) {
                //Logger.Instance.Info(`🎬 Creating CSS3D YouTube player in 3D space: ${this.name}`);
                this.createCSS3DYouTubePlayer();
            }
        }
    }

    private setupYouTubeTextureCapture(): void {
        if (this._iframe || !this._videoId) return;
        
        //Logger.Instance.Info(`🎬 Creating HIDDEN iframe for 3D texture capture: ${this._videoId}`);
        
        // Create HIDDEN iframe for video capture to canvas texture
        this._iframe = document.createElement('iframe');
        this._iframe.width = '1280';
        this._iframe.height = '720';
        this._iframe.style.position = 'absolute';
        this._iframe.style.left = '-9999px'; // Hidden off-screen
        this._iframe.style.top = '-9999px';
        this._iframe.style.width = '1280px';
        this._iframe.style.height = '720px';
        this._iframe.style.border = 'none';
        this._iframe.style.visibility = 'hidden'; // Extra hidden
        this._iframe.allow = 'autoplay; encrypted-media';
        
        // Build embed URL with autoplay
        const embedUrl = this.buildYouTubeEmbedUrl(this._videoId);
        this._iframe.src = embedUrl;
        
        // Add to DOM (hidden)
        document.body.appendChild(this._iframe);
        
        // Wait for iframe to load, then start video capture
        this._iframe.onload = () => {
            //Logger.Instance.Info(`🎬 YouTube iframe loaded, starting 3D texture capture`);
            this._videoMode = 'iframe';
            
            // Start video playback
            if (this.playOnActive.value) {
                setTimeout(() => {
                    this._iframe?.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                    this._isPlaying = true;
                    //Logger.Instance.Info(`🎬 YouTube video playing on 3D texture`);
                }, 1000);
            }
        };
        
        //console.log(`✅ Hidden YouTube iframe created for 3D texture: ${this._videoId}`);
    }

    private createCSS3DYouTubePlayer(): void {
        if (this._css3dObject || !this._videoId) return;

        //Logger.Instance.Info(`🎬 Creating CSS3D YouTube iframe for 3D positioning: ${this._videoId}`);
        
        // Create iframe for YouTube video
        const iframe = document.createElement('iframe');
        
        // Match iframe size to POI size (if available)
        let iframeWidth = 1280;
        let iframeHeight = 720;
        
        // Try to get size from shape geometry or use aspect ratio
        if (this.poi && this.poi.shape && this.poi.shape._geometry) {
            try {
                const geometry = this.poi.shape._geometry;
                const boundingBox = new THREE.Box3().setFromBufferAttribute(geometry.getAttribute('position'));
                const size = boundingBox.getSize(new THREE.Vector3());
                
                // Convert shape size to iframe pixels (scale up for better quality)
                const pixelScale = 200; // 200 pixels per unit
                iframeWidth = Math.max(640, size.x * pixelScale);
                iframeHeight = Math.max(360, size.y * pixelScale);
                
                //Logger.Instance.Info(`🎯 CSS3D iframe sized based on shape: ${iframeWidth}x${iframeHeight} (shape size: ${size.x.toFixed(2)}x${size.y.toFixed(2)})`);
            } catch (error) {
                Logger.Instance.Debug(`🎯 Could not get shape size, using default iframe size: ${iframeWidth}x${iframeHeight}`);
            }
        } else if (this.poi && this.poi.poidata.aspectRatio && this.poi.poidata.aspectRatio.value > 0) {
            // Use aspect ratio if available
            const aspectRatio = this.poi.poidata.aspectRatio.value;
            iframeHeight = 720;
            iframeWidth = Math.round(iframeHeight * aspectRatio);
            //Logger.Instance.Info(`🎯 CSS3D iframe sized based on aspect ratio: ${iframeWidth}x${iframeHeight} (ratio: ${aspectRatio.toFixed(2)})`);
        } else {
            Logger.Instance.Debug(`🎯 No size information found, using default iframe size: ${iframeWidth}x${iframeHeight}`);
        }
        
        iframe.width = iframeWidth.toString();
        iframe.height = iframeHeight.toString();
        iframe.style.border = 'none';
        iframe.style.backgroundColor = 'transparent';
        iframe.allow = 'autoplay; encrypted-media; fullscreen';
        iframe.setAttribute('allowfullscreen', '');
        
        // Build YouTube embed URL
        const embedUrl = this.buildYouTubeEmbedUrl(this._videoId!);
        iframe.src = embedUrl;
        
        // Create CSS3DObject for 3D positioning
        this._css3dObject = new CSS3DObject(iframe);
        this._css3dObject.name = `YouTube_${this._videoId}`;
        
        // Scale to match the POI size (similar to material texture)
        const scale = 0.01; // Adjust scale to match your material size
        this._css3dObject.scale.set(scale, scale, scale);
        
        // Position CSS3D object at POI's world position
        this.positionCSS3DAtPOI();
        
        // Add to CSS3D scene
        Scene.Instance.addCSS3DObject(this._css3dObject);
        this._videoMode = 'css3d';
        
        // Auto-play if enabled
        if (this.playOnActive.value && !this._manuallyPaused) {
            setTimeout(() => {
                iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                this._isPlaying = true;
                //Logger.Instance.Info(`🎬 CSS3D YouTube video auto-playing: ${this._videoId}`);
            }, 1000);
        }
        
        // //Logger.Instance.Info(`✅ CSS3D YouTube player created and positioned in 3D space: ${this._videoId}`);
        
        // Update position continuously (in case POI moves)
        this.startPositionUpdates();
    }

    private startPositionUpdates(): void {
        if (!this._css3dObject) return;
        
        // Update position every frame while CSS3D is active
        const updatePosition = () => {
            if (this._css3dObject && this._videoMode === 'css3d') {
                this.positionCSS3DAtPOI();
                requestAnimationFrame(updatePosition);
            }
        };
        
        requestAnimationFrame(updatePosition);
    }

    private positionCSS3DAtPOI(): void {
        if (!this._css3dObject || !this.poi) return;

        try {
            // Get POI shape (the 3D object)
            const shape = this.poi.shape;
            if (!shape || !shape._mesh) {
                Logger.Instance.Warn(`🎯 No shape found for POI ${this.poi.name}, using POI transform`);
                
                // Fallback to POI transform
                const poiPos = this.poi.transform.worldPosition.value;
                const poiRot = this.poi.transform.worldQuaternion.value;
                const poiScale = this.poi.transform.worldScale.value;
                
                this._css3dObject.position.copy(poiPos);
                this._css3dObject.quaternion.copy(poiRot);
                
                // Scale based on POI size with adaptive scaling
                const avgScale = (poiScale.x + poiScale.y + poiScale.z) / 3;
                let scaleMultiplier = 0.01;
                
                if (avgScale > 10) {
                    scaleMultiplier = 0.001;
                } else if (avgScale > 5) {
                    scaleMultiplier = 0.005;
                } else if (avgScale > 1) {
                    scaleMultiplier = 0.01;
                } else {
                    scaleMultiplier = 0.02;
                }
                
                // Apply manual scale override if set
                const finalScale = scaleMultiplier * (this.css3dScale.value || 1.0);
                this._css3dObject.scale.set(
                    poiScale.x * finalScale,
                    poiScale.y * finalScale,
                    poiScale.z * finalScale
                );
                
                Logger.Instance.Debug(`🎯 CSS3D positioned using POI transform: pos=${poiPos.x.toFixed(2)}, ${poiPos.y.toFixed(2)}, ${poiPos.z.toFixed(2)}`);
                return;
            }
            
            // Use shape's world transform (most accurate)
            const shapeMesh = shape._mesh;
            shapeMesh.updateMatrixWorld(true); // Ensure world matrix is up-to-date
            
            // Extract position, rotation, scale from world matrix
            const position = new THREE.Vector3();
            const quaternion = new THREE.Quaternion();
            const scale = new THREE.Vector3();
            shapeMesh.matrixWorld.decompose(position, quaternion, scale);
            
            // Apply to CSS3D object
            this._css3dObject.position.copy(position);
            this._css3dObject.quaternion.copy(quaternion);
            
            // Scale: CSS3D needs much smaller scale than WebGL
            // Try different scale multipliers based on shape size
            let scaleMultiplier = 0.01;
            
            // Adaptive scaling based on shape size
            const avgScale = (scale.x + scale.y + scale.z) / 3;
            if (avgScale > 10) {
                scaleMultiplier = 0.001; // Very large shapes need smaller CSS3D scale
            } else if (avgScale > 5) {
                scaleMultiplier = 0.005; // Large shapes
            } else if (avgScale > 1) {
                scaleMultiplier = 0.01;  // Normal shapes
            } else {
                scaleMultiplier = 0.02;  // Small shapes need larger CSS3D scale
            }
            
            // Apply manual scale override if set
            const finalScale = scaleMultiplier * (this.css3dScale.value || 1.0);
            this._css3dObject.scale.set(
                scale.x * finalScale,
                scale.y * finalScale, 
                scale.z * finalScale
            );
            
            //Logger.Instance.Info(`🎯 CSS3D YouTube positioned at shape location:`);
            //Logger.Instance.Info(`  Position: ${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}`);
            //Logger.Instance.Info(`  Scale: ${scale.x.toFixed(2)}, ${scale.y.toFixed(2)}, ${scale.z.toFixed(2)} -> CSS3D: ${(scale.x * scaleMultiplier).toFixed(4)}`);
            
        } catch (error) {
            Logger.Instance.Error(`Failed to position CSS3D YouTube: ${error as string}`);
            // Fallback to origin
            this._css3dObject.position.set(0, 0, 0);
            this._css3dObject.rotation.set(0, 0, 0);
            this._css3dObject.scale.set(0.01, 0.01, 0.01);
        }
    }

    public override setInactive(): void {
        // Always pause when becoming inactive, but preserve manual pause state
        if (this._isPlaying) {
            Logger.Instance.Debug(`🎬 YouTube pausing due to POI becoming inactive: ${this.name}`);
            this.pause();
        }
        
        // Hide CSS3D player when POI becomes inactive
        if (this._css3dObject && this._videoMode === 'css3d') {
            Logger.Instance.Debug(`🎬 Hiding CSS3D YouTube player: ${this.name}`);
            Scene.Instance.removeCSS3DObject(this._css3dObject);
            this._css3dObject = null;
            this._videoMode = 'thumbnail';
        }
    }

    // Convenience methods
    public play(): void {
        if (this._videoElement && this._videoMode === 'proxy') {
            // Real video playback with THREE.VideoTexture
            this._videoElement.play().then(() => {
                this._isPlaying = true;
                this._manuallyPaused = false;
                //console.log(`▶️ YouTubeMaterial: Playing real video ${this._videoId}`);
            }).catch(error => {
                console.warn(`Failed to play video:`, error);
            });
        } else if (this._css3dObject && this._videoMode === 'css3d') {
            // CSS3D iframe playback in 3D space
            const iframe = this._css3dObject.element as HTMLIFrameElement;
            iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            this._isPlaying = true;
            this._manuallyPaused = false;
            //console.log(`▶️ YouTubeMaterial: Playing CSS3D video ${this._videoId} in 3D space`);
        } else if (this._iframe) {
            // Fallback to iframe
            this.showYouTubePlayer();
            this._iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            this._isPlaying = true;
            this._manuallyPaused = false;
            //console.log(`▶️ YouTubeMaterial: Playing video ${this._videoId} in overlay`);
        }
    }

    public pause(): void {
        if (this._videoElement && this._videoMode === 'proxy') {
            // Real video pause
            this._videoElement.pause();
            this._isPlaying = false;
            //console.log(`⏸️ YouTubeMaterial: Paused real video ${this._videoId}`);
        } else if (this._css3dObject && this._videoMode === 'css3d') {
            // CSS3D iframe pause in 3D space
            const iframe = this._css3dObject.element as HTMLIFrameElement;
            iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            this._isPlaying = false;
            //console.log(`⏸️ YouTubeMaterial: Paused CSS3D video ${this._videoId}`);
        } else if (this._iframe) {
            // Fallback to iframe
            this._iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            this._isPlaying = false;
            //console.log(`⏸️ YouTubeMaterial: Paused video ${this._videoId}`);
        }
    }

    public togglePlayPause(): void {
        if (this._isPlaying) {
            this._manuallyPaused = true;
            this.pause();
        } else {
            this.play();
        }
    }

    public dispose(): void {
        this.disposeTexture();
        super.dispose();
    }

    // Factory method
    static create(params: any = {}): YouTubeMaterial {
        const poiData = params.poiData || new POIData();
        const material = new YouTubeMaterial(params.name || 'YouTube Material', poiData);
        
        if (params.uri) {
            material.setUri(params.uri);
        }
        
        return material;
    }
}